import * as React from 'react';
import { useMutation } from 'react-query';
import type { DeliveryVoucher } from 'contracts/DeliveryVouchers';
import { useDeliveryVoucher } from 'hooks/api/delivery-vouchers/use-delivery-voucher';
import { Formatter } from 'utils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';

export type ViewDeliveryVoucherDialogRef = {
  open: (voucher: DeliveryVoucher) => void;
};

const isPdfFile = (fileName?: string) => {
  return !!fileName && fileName.toLowerCase().endsWith('.pdf');
};

const formatDate = (value?: string | null) => {
  return value ? Formatter.date(value, { format: 'dd/MM/yyyy HH:mm' }) : '---';
};

export const ViewDeliveryVoucherDialog = React.forwardRef<
  ViewDeliveryVoucherDialogRef
>((_, ref) => {
  const [open, setOpen] = React.useState(false);
  const [voucher, setVoucher] = React.useState<DeliveryVoucher>();
  const { fetchVoucherFile } = useDeliveryVoucher();

  const voucherFileMutation = useMutation(fetchVoucherFile);
  const {
    mutate: fetchVoucherFileUrl,
    reset: resetVoucherFile,
    data: file,
    isError,
    isLoading,
  } = voucherFileMutation;

  const handleOpen = React.useCallback(
    (selectedVoucher: DeliveryVoucher) => {
      document.body.style.pointerEvents = '';
      document.body.removeAttribute('data-scroll-locked');
      setVoucher(selectedVoucher);
      setOpen(true);
      // Busca a URL assinada somente quando o usuário decide visualizar.
      fetchVoucherFileUrl(selectedVoucher.id);
    },
    [fetchVoucherFileUrl]
  );

  const handleOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen);

      if (!nextOpen) {
        // Ao fechar, remove o comprovante selecionado e a URL assinada carregada.
        setVoucher(undefined);
        resetVoucherFile();
      }
    },
    [resetVoucherFile]
  );

  React.useImperativeHandle(ref, () => ({ open: handleOpen }), [handleOpen]);

  const fileIsPdf = isPdfFile(file?.fileName || voucher?.fileName);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="w-[95vw] max-w-[960px] bg-white">
        
        <DialogHeader className="pr-8 text-left">
          <DialogTitle>Visualizar comprovante</DialogTitle>
          
          {voucher && (
            <div className="mt-2 flex flex-col gap-1 text-sm text-slate-600 font-sans">
              <p>
                Criado em: {formatDate(voucher.createdAt)} - Enviado por: {voucher.userSender?.name || '---'}
              </p>
              
              {voucher.apiReason && voucher.approvalApi && (
                <p className="text-slate-600">
                  ({voucher.approvalApi}) - {voucher.apiReason}
                </p>
              )}

              {voucher.approvedAt && (
                <p className="text-green-600">
                  Aprovado por: {voucher.updatedBy?.name || '---'}
                </p>
              )}

              {voucher.rejectedAt && (
                <p className="text-red-600">
                  Reprovado por: {voucher.updatedBy?.name || '---'}
                </p>
              )}
            </div>
          )}
        </DialogHeader>

        {isLoading && (
          <div className="flex h-[60vh] items-center justify-center rounded-md border text-sm text-muted-foreground">
            Carregando comprovante...
          </div>
        )}

        {isError && (
          <div className="flex h-[220px] items-center justify-center rounded-md border text-sm text-red-600">
            Não foi possível carregar o comprovante.
          </div>
        )}

        {file && !isLoading && !isError && (
          <>
            {fileIsPdf ? (
              <iframe
                src={file.url}
                title={file.fileName}
                className="h-[72vh] w-full rounded-md border"
              />
            ) : (
              <div className="flex max-h-[72vh] items-center justify-center overflow-auto rounded-md border bg-slate-50 p-4">
                <img
                  src={file.url}
                  alt={file.fileName}
                  className="max-h-[68vh] max-w-full object-contain"
                />
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
});

ViewDeliveryVoucherDialog.displayName = 'ViewDeliveryVoucherDialog';
